import { Directive, ElementRef, Input, Output, EventEmitter, Renderer2 } from '@angular/core';

@Directive({
  selector: '[appOutSideClick]',
  standalone: true,
})
export class OutsideClickDirective {
  /** When true, outside-click is disabled (e.g. for date picker in filter UI so calendar can receive clicks). */
  @Input() set appOutSideClickDisabled(value: boolean) {
    this._disabled = value;
  }
  private _disabled = false;

  @Output() outSideClick: EventEmitter<void> = new EventEmitter();
  constructor(
    private element: ElementRef,
    private renderer: Renderer2
  ) {}

  private listener: (() => void) | undefined;
  // Specify the class you want to check on the event target
  private excludeClasses: string[] = [
    'cdk-overlay-backdrop',
    'column-item',
    'column-name',
    'container-header-custom',
    'header-title',
    'mat-mdc-menu-content',
    'container-header',
    'columns-count',
    'columns-list',
    'option-search-container',
    'dropdown-content-container',
    'container-header-data',
    'count',
    'custom-file-input',
    'custom-button-style',
    'file-upload-icon',
    'icon',
    'close-icon',
    'datepicker-class',
    'datepicker-trigger-row',
    'button-wrapper',
    'yearselect',
    'monthselect',
    'calendar',
    'daterangepicker',
  ];

  // Execute this function when click outside of the dropdown-container
  onDocumentClick = (event: Event) => {
    if (this._disabled) return;

    const target = event.target as HTMLElement;

    // If the event target has the exclude class, return early (do nothing)
    if (this.excludeClasses.some((cls) => target.classList.contains(cls))) {
      return;
    }

    // Clicks inside the date range picker calendar should not close the picker (calendar is rendered outside the input's parent)
    if (target.closest?.('.daterangepicker')) {
      return;
    }
    // Clicks inside the Angular Material datepicker calendar (simple-date-picker) so month/year navigation works
    if (target.closest?.('.mat-datepicker-content') || target.closest?.('.mat-calendar')) {
      return;
    }
    // Additional check for any select elements within date picker contexts
    if (target.tagName === 'SELECT' && target.closest?.('.daterangepicker')) {
      return;
    }
    // Check for year selection dropdowns and options
    if (target.closest?.('select') && target.closest?.('.calendar')) {
      return;
    }
    // Check for any element that might be part of year selection UI
    if (target.closest?.('.yearselect') || target.closest?.('.monthselect')) {
      return;
    }
    // Check for clicks on any calendar-related elements including tables, th, td within calendar
    if (target.closest?.('table') && target.closest?.('.daterangepicker')) {
      return;
    }
    // Check for clicks on calendar navigation elements
    if (target.closest?.('.calendar') && (target.closest?.('th') || target.closest?.('td'))) {
      return;
    }
    // Check for any element with calendar-related classes
    if (target.closest?.('[class*="calendar"]') || target.closest?.('[class*="date"]')) {
      return;
    }

    if (!this.element.nativeElement.parentElement.contains(event.target)) {
      this.outSideClick.emit();
    }
  };

  //Add the listener when the dropdown component is rendered
  ngOnInit(): void {
    this.listener = this.renderer.listen('document', 'click', this.onDocumentClick);
  }

  //To reduce unnecessary memory leaks you need to use the clean-up
  ngOnDestroy(): void {
    if (this.listener) {
      this.listener();
    }
  }
}
